import { getApiBaseUrl } from "./base-url";

type RequestConfig = {
  url: string;
  method: string;
  params?: Record<string, unknown>;
  data?: unknown;
  headers?: HeadersInit;
  signal?: AbortSignal;
};

export class ApiError extends Error {
  status: number;
  statusText: string;
  data: unknown;
  contentType: string | null;

  constructor(args: { status: number; statusText: string; data: unknown; contentType: string | null }) {
    super(`Request failed (${args.status})`);
    this.name = "ApiError";
    this.status = args.status;
    this.statusText = args.statusText;
    this.data = args.data;
    this.contentType = args.contentType;
  }
}

async function parseResponseData(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return undefined;
  }

  const contentType = response.headers.get("Content-Type")?.toLowerCase() ?? "";

  if (contentType.includes("application/json") || contentType.includes("+json")) {
    return response.json();
  }

  const text = await response.text();
  return text.length > 0 ? text : undefined;
}

function toRequestBody(data: unknown): BodyInit | undefined {
  if (data === undefined) {
    return undefined;
  }

  if (
    typeof data === "string" ||
    data instanceof URLSearchParams ||
    data instanceof FormData ||
    data instanceof Blob ||
    data instanceof ArrayBuffer
  ) {
    return data;
  }

  return JSON.stringify(data);
}

function appendParams(url: URL, params?: Record<string, unknown>) {
  if (!params) {
    return;
  }

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) {
      continue;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null) {
          url.searchParams.append(key, String(item));
        }
      }
      continue;
    }

    url.searchParams.append(key, String(value));
  }
}

export async function apiMutator<TData>(config: RequestConfig): Promise<TData> {
  const baseUrl = getApiBaseUrl();
  const requestUrl = new URL(config.url, baseUrl);
  appendParams(requestUrl, config.params);

  const response = await fetch(requestUrl, {
    method: config.method,
    body: toRequestBody(config.data),
    headers: {
      "Content-Type": "application/json",
      ...config.headers,
    },
    credentials: "include",
    signal: config.signal,
  });

  if (!response.ok) {
    const data = await parseResponseData(response).catch(() => undefined);
    throw new ApiError({
      status: response.status,
      statusText: response.statusText,
      data,
      contentType: response.headers.get("Content-Type"),
    });
  }

  return (await parseResponseData(response)) as TData;
}
